import { t } from 'i18next'
import React from 'react'
import { Trans } from 'react-i18next'

import { InvoicingSystem, PlanCode } from '../../common/enums.js'
import { BasicViewPaths, MockedSubscriptionStatus, SubscriptionSteps } from '../../common/types.js'
import { roundPrice } from '../../common/utils/maths.js'
import { formatMonthlyPrice, formatPrice } from '../../common/utils/utils.js'
import { CartProps } from '../components-hydrated/cart/cart.js'
import { PlanCardsProps } from '../components-hydrated/plan-cards/plan-cards.js'
import { CartGridProps, CartGridRowProps } from '../components/cart/cart.js'
import { ButtonGroupOption } from '../components/forms/button-group/button-group.js'
import { OptionCardGroupValue } from '../components/forms/option-card-group/option-card-group.js'
import { RadioCardProps } from '../components/forms/radio-card/radio-card.js'
import { scrollToElement } from '../components/helper/index.js'
import { ProductProps } from '../components/product/product.js'
import { ProgressProps } from '../components/progress/progress.js'
import { AuthProps } from '../components/steps/authentication-step.js'
import { CreditCheckProps } from '../components/steps/credit-check-step.js'
import { SignProps } from '../components/steps/sign-step.js'
import { ValidationProps } from '../components/steps/validation-step.js'
import { DeliveryMethod } from '../models/delivery-method.js'
import { SubscriptionValueProduct } from '../models/subscription-value-product.js'
import { Subscription } from '../models/subscription.js'
import {
    IFrameValidationMessage,
    IFrameValidationUpdate,
    Route,
    SubscriptionRoute,
    SubscriptionUuidViewPaths,
    View,
} from '../types.js'
import { getTranslatedErrorMessage } from '../utils/utils.js'
import { SubscriptionProps } from '../views/subscription/subscription.js'

const getPossibleAuthMethods = (subscription: Subscription): ButtonGroupOption[] => {
    return (subscription.possibleAuthenticationMethods || []).map((method) => {
        return {
            id: `${method.providerName}-${method.bankName}`,
            value: `${method.providerName}_${method.bankName || ''}`,
            text: method.visualName || method.providerName,
        }
    })
}

const getAuthenticationProps = (route: SubscriptionRoute, subscription: Subscription, view: View): AuthProps => {
    const possibleAuthMethods = getPossibleAuthMethods(subscription)
    const isLisaAnd = subscription.invoicingSystem === InvoicingSystem.LISAAND

    const base = {
        title: t('authStep.title'),
        subtitle: t('authStep.subtitle'),
        pepTermsCheckbox: isLisaAnd
            ? undefined
            : {
                  id: 'pep-checkbox',
                  value: 'pep-terms',
                  label: t('authStep.pepTerms'),
                  checked: !!view.state.formData.pepTerms,
                  onChange: (isChecked: boolean) => {
                      view.state.formData.pepTerms = isChecked
                      view.update()
                  },
              },
    }

    if (possibleAuthMethods.length > 1) {
        return {
            ...base,
            authenticationMethodField: {
                select: {
                    id: 'authenticationMethod',
                    name: t('authStep.authenticationMethod'),
                    value: view.state.formData.authenticationMethod || possibleAuthMethods[0].value,
                    options: possibleAuthMethods.map((method) => ({
                        id: method.id,
                        label: method.text,
                        value: method.value || '',
                    })),
                    onChange: (value) => {
                        if (value) {
                            view.state.formData.authenticationMethod = value as string
                            view.update()
                        }
                    },
                    label: t('authStep.authSelect'),
                    bordered: true,
                },
            },
            authenticationMethodButtons:
                isLisaAnd && view.state.formData.authenticationMethod
                    ? {
                          id: 'authenticationMethod',
                          name: t('authStep.authenticationMethod'),
                          value: view.state.formData.authenticationMethod,
                          options: possibleAuthMethods
                              .filter((method) => method.value === view.state.formData.authenticationMethod)
                              .map((method) => ({
                                  id: method.id,
                                  text: `${t('authStep.loginWith')} ${method.text}`,
                                  value: method.value || '',
                                  variant: 'tertiary',
                                  displayIcon: false,
                                  onClick: () => {
                                      submitCart(route, subscription, view)
                                  },
                                  element: 'button',
                              })),
                          onChange: (value) => {
                              if (value) {
                                  view.state.formData.authenticationMethod = value
                                  view.update()
                              }
                          },
                      }
                    : undefined,
        }
    }

    return {
        ...base,
        authenticationMethodButtons: {
            id: 'authenticationMethod',
            name: t('authStep.authenticationMethod'),
            value: view.state.formData.authenticationMethod || possibleAuthMethods[0].value,
            options: possibleAuthMethods.map((method) => ({
                id: method.id,
                text: isLisaAnd ? `${t('authStep.loginWith')} ${method.text}` : method.text,
                value: method.value || '',
                variant: isLisaAnd ? 'tertiary' : undefined,
                displayIcon: isLisaAnd ? false : undefined,
                onClick: isLisaAnd
                    ? () => {
                          view.state.formData.authenticationMethod = (view.state.formData.authenticationMethod ||
                              possibleAuthMethods[0].value)!

                          view.update()

                          submitCart(route, subscription, view)
                      }
                    : undefined,
                element: isLisaAnd ? 'button' : undefined,
            })),
            onChange: (value) => {
                if (value) {
                    view.state.formData.authenticationMethod = value
                    view.update()
                }
            },
        },
    }
}

const submitCart = (route: SubscriptionRoute, subscription: Subscription, view: View): void => {
    const hasCustomer = !!subscription.customer
    const isLisaAndFlow = subscription.invoicingSystem === InvoicingSystem.LISAAND
    const pepTermsMet = isLisaAndFlow || view.state.formData.pepTerms
    const authConditionsMet = !!(view.state.formData.authenticationMethod && pepTermsMet)
    const signConditionsMet = getSignConditionsMet(view)
    const creditCheckConditionsMet = getCreditCheckConditionsMet(view)

    switch (route.step) {
        case SubscriptionSteps.PLANS:
            view.api.selectSubscriptionPlan().catch(() => false)

            if (isLisaAndFlow) {
                view.api.startLisaAndFlow(subscription.uuid).catch(() => false)
            } else {
                if (hasCustomer) {
                    window.location.hash = `#/subscription/${subscription.uuid}/${SubscriptionSteps.VALIDATION}`
                } else {
                    window.location.hash = `#/subscription/${subscription.uuid}/${SubscriptionSteps.AUTH}`
                }
            }

            view.update()
            break
        case SubscriptionSteps.AUTH:
            if (authConditionsMet) {
                view.api.auth().catch(() => false)
            }
            break
        case SubscriptionSteps.SIGN:
            // Open modal
            if (signConditionsMet) {
                view.state.fraudModalOpen = true
                view.update()
                view.api
                    .fraudProtection()
                    .then((response) => {
                        view.state.fraudModalOpen = false
                        if (response.success) {
                            view.api.sign().catch(() => false)
                        } else if (response.success === false) {
                            window.location.hash = `#/${BasicViewPaths.CREDIT_FAILED}`
                        }

                        view.update()
                        return
                    })
                    .catch((_error) => {
                        window.location.hash = `#/${BasicViewPaths.CREDIT_FAILED}`
                        view.update()
                        return
                    })
            }
            break
        case SubscriptionSteps.CREDIT_CHECK:
            if (creditCheckConditionsMet) {
                view.api.creditCheck().catch(() => false)
            }
            break
    }

    scrollToElement('root')
}

const formatShippingMethod = (method: DeliveryMethod): RadioCardProps => ({
    id: method.uuid,
    labelText: method.title,
    description: method.description,
    value: method.uuid,
})

const getSignProps = (subscription: Subscription, view: View): SignProps | undefined => {
    return {
        firstNameField: {
            id: 'firstName',
            name: 'firstName',
            label: t('sign.firstName'),
            error: getTranslatedErrorMessage('sign.errors.firstName', view.state.formErrors.firstName),
            value: view.state.formData.firstName,
            isRequired: true,
            isDisabled: !subscription.canEditName(),
            onChange: (value) => {
                view.state.formData.firstName = value
                view.update()
            },
        },
        lastNameField: {
            id: 'lastName',
            name: 'lastName',
            label: t('sign.lastName'),
            error: getTranslatedErrorMessage('sign.errors.lastName', view.state.formErrors.lastName),
            value: view.state.formData.lastName,
            isRequired: true,
            isDisabled: !subscription.canEditName(),
            onChange: (value) => {
                view.state.formData.lastName = value
                view.update()
            },
        },
        emailField: {
            id: 'email',
            name: 'email',
            label: t('sign.email'),
            error: getTranslatedErrorMessage('sign.errors.email', view.state.formErrors.email),
            value: view.state.formData.email,
            type: 'email',
            isRequired: true,
            onChange: (value) => {
                view.state.formData.email = value
                view.update()
            },
        },
        phoneField: {
            id: 'phone',
            name: 'phone',
            label: t('sign.phone'),
            error: getTranslatedErrorMessage('sign.errors.phone', view.state.formErrors.phone),
            value: view.state.formData.phone,
            type: 'phone',
            isRequired: true,
            onChange: (value) => {
                view.state.formData.phone = value
                view.update()
            },
        },
        postField: {
            id: 'postalcode',
            name: 'postalcode',
            label: t('sign.postalCode'),
            error: getTranslatedErrorMessage('sign.errors.postalCode', view.state.formErrors.postalCode),
            value: view.state.formData.postalCode,
            type: 'text',
            isRequired: true,
            // TEMP: Lock shipping if defined by scrive
            isDisabled: view.state.formData.postalCodeLock,
            onChange: (value) => {
                view.state.formData.postalCode = value
                view.update()
            },
        },
        addressField: {
            id: 'address',
            name: 'address',
            label: t('sign.address'),
            error: getTranslatedErrorMessage('sign.errors.address', view.state.formErrors.address),
            value: view.state.formData.address,
            type: 'text',
            isRequired: true,
            // TEMP: Lock shipping if defined by scrive
            isDisabled: view.state.formData.addressLock,
            onChange: (value) => {
                view.state.formData.address = value
                view.update()
            },
        },
        cityField: {
            id: 'city',
            name: 'city',
            label: t('sign.city'),
            error: getTranslatedErrorMessage('sign.errors.city', view.state.formErrors.city),
            value: view.state.formData.city,
            type: 'text',
            isRequired: true,
            // TEMP: Lock shipping if defined by scrive
            isDisabled: view.state.formData.cityLock,
            onChange: (value) => {
                view.state.formData.city = value
                view.update()
            },
        },
        shippingTitle: t('sign.delivery'),
        // TEMP: Lock shipping if defined by scrive
        // differentShippingAddress: {
        //     id: 'use-another-shipping',
        //     label: t('sign.differentShippingAddress'),
        //     value: '1',
        //     checked: view.state.formData.differentShippingAddress,
        //     onChange: (value) => {
        //         view.state.formData.differentShippingAddress = value
        //         view.update()
        //     },
        // },
        // differentShippingFields: subscription.deliveryMethod?.getAdditionalFields(view),
        shippingMethods: {
            groupProps: {
                id: 'shipping-methods',
                name: 'shipping-methods',
                value: view.state.formData.shippingMethodUuid,
                onChange: (value) => {
                    if (view.state.subscription) {
                        view.state.formData.shippingMethodUuid = value!
                        view.state.subscription.updateDeliveryMethod(view.state.formData.shippingMethodUuid)
                        view.update()
                    }
                },
                options: subscription.deliveryMethods.map(formatShippingMethod),
            },
        },
        acceptTerms: {
            label: t('sign.acceptTermsLabel'),
            id: 'accept-terms',
            hiddenDescription: (
                <span>
                    {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                    {/* @ts-ignore ReactI18N type definition issue */}
                    <Trans i18nKey="sign.acceptTerms">
                        By choosing to receive marketing messages from Samsung, we will share information about you and
                        your order with Samsung Electronics (UK) Limited and Samsung Electronics Co., Ltd. (Korea){' '}
                        <a target="_blank" href={t('sign.acceptTermsLink')} rel="noreferrer">
                            In accordance with Samsung's Global Privacy Policy.
                        </a>
                        We, Samsung Electronics Nordic AB of the above Samsung entities on behalf of you, we provide you
                        with customized advertising, news, special offers and other information about Samsung and its
                        partners' products and services via email, text messages and notifications. You can unsubscribe
                        from messages at any time in your Samsung account settings or by clicking "Unsubscribe here" at
                        the bottom of the newsletter.
                    </Trans>
                </span>
            ),
            checked: view.state.formData.marketingTerms,
            value: 'marketingTerms',
            onChange: (checked) => {
                view.state.formData.marketingTerms = checked
                view.update()
            },
        },
    }
}

export const isIFrameValidationUpdate = (update: unknown): update is IFrameValidationUpdate => {
    return !!(update && update instanceof Object && 'message' in update)
}

export const onValidationMessageUpdate = (view: View, update: IFrameValidationUpdate) => {
    view.initValidationTimeout()

    // force reload of iframe, kinda haxy but it works
    switch (update.message) {
        case IFrameValidationMessage.CANCELLED:
            view.state.validation.iframeKey = update.message
            break

        case IFrameValidationMessage.FINISHED:
        case IFrameValidationMessage.FLOW_COMPLETED:
            view.state.validation.iframeKey = update.message
            view.state.validation.isLoading = true
            break

        case IFrameValidationMessage.RELOAD:
            window.location.reload()
            break
    }

    view.update()
}

const getValidationProps = (route: Route, subscription: Subscription, view: View): ValidationProps => {
    if (route.view === SubscriptionUuidViewPaths.SUBSCRIPTION && route.step === SubscriptionSteps.VALIDATION) {
        view.initValidationTimeout()
    }

    return {
        title: t('validationStep.title'),
        subtitle: t('validationStep.subtitle'),
        loadingText: t('validationStep.loadingText'),
        iframeUrl: subscription.validationIframeUrl,
        iframeKey: view.state.validation.iframeKey,
        isLoading: view.state.validation.isLoading,
    }
}

const getCreditCheckProps = (subscription: Subscription, view: View): CreditCheckProps => {
    return {
        title: t('creditCheckStep.title'),
        subtitle: t('creditCheckStep.subtitle'),
        personalNumberField: {
            id: 'personalNumber',
            name: 'personalNumber',
            label: t('creditCheckStep.personalNumber'),
            error: view.state.formErrors.personalNumber ? t('creditCheckStep.errors.personalNumber') : undefined,
            value: view.state.formData.personalNumber,
            isRequired: true,
            onChange: (value) => {
                view.state.formData.personalNumber = value
                view.update()
            },
        },
        termsCheckbox: {
            id: 'credit-check-terms-checkbox',
            value: 'creditCheckTerms',
            label: (
                <span>
                    {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                    {/* @ts-ignore ReactI18N type definition issue */}
                    <Trans i18nKey="creditCheckStep.terms">
                        I agree to send my information to{' '}
                        <a href={t('creditCheckStep.termsLink')}>lorem ipsum dolor sit</a> in order to get a credit
                        check evaluation.
                    </Trans>
                </span>
            ),
            checked: !!view.state.formData.creditCheckTerms,
            onChange: (isChecked: boolean) => {
                view.state.formData.creditCheckTerms = isChecked
                view.update()
            },
        },
    }
}

const getPlansProps = (subscription: Subscription, view: View): PlanCardsProps => {
    const welcomeTitle = subscription.getTranslation('plans.welcomeMessage.title')
    const welcomeParagraphs: string[] = subscription.getTranslation('plans.welcomeMessage.paragraphs', {
        returnObjects: true,
    }) as unknown as []
    return {
        title: subscription.getTranslation('plans.selectSubscription'),
        description: subscription.getTranslation('plans.description', {
            months: subscription.mainProduct.plan.duration,
        }),
        plans: subscription.planCardProps(view),
        welcomeMessage:
            welcomeParagraphs || welcomeTitle
                ? {
                      title: welcomeTitle,
                      paragraphs: welcomeParagraphs,
                  }
                : undefined,
        disclaimer: subscription.hasTradeIn ? subscription.getTranslation('plans.disclaimer') : undefined,
        addOns: [
            ...subscription.data.localizedPossibleAddons.map((addOn) => ({
                ...addOn,
                isSelected: subscription.data.localizedSetAddOns.some((setAddOn) => setAddOn.uuid === addOn.uuid),
                onChange: (checked: boolean) => {
                    console.log(`Toggling add-on ${addOn.uuid} to ${checked}`)
                    view.api
                        .setSubscriptionAddOn(view.state.subscription?.uuid + '', addOn.uuid, checked)
                        .catch(() => {
                            view.addNotification({
                                children: <p>{t('errors.addOnToggleError')}</p>,
                                isDefaultOpen: true,
                                duration: 4000,
                                type: 'error',
                            })
                        })
                        .finally(() => {
                            view.state.isLoading = false
                            view.update()
                        })
                },
            })),
        ],
        groupProps: {
            children: '',
            id: subscription.uuid,
            inputType: 'radio',
            name: subscription.uuid,
            value: view.state.selectedPlan,
            onChange: (value: OptionCardGroupValue) => {
                view.state.isLoading = true
                view.state.selectedPlan = value as string
                view.state.selectedPlans.push(view.state.selectedPlan)
                view.update()

                view.api
                    .setSubscriptionPlan(view.state.subscription?.uuid + '', value as string)
                    .catch(() => {
                        view.addNotification({
                            children: <p>{t('errors.planChangeError')}</p>,
                            isDefaultOpen: true,
                            duration: 4000,
                            type: 'error',
                        })
                    })
                    .finally(() => {
                        view.state.isLoading = false
                        view.update()
                    })
            },
        },
    }
}

const getCartProducts = (subscription: Subscription): Array<ProductProps | CartGridProps> => {
    // ANCHOR: Decision based on InvoicingSystem
    if (subscription.data.invoicingSystem === InvoicingSystem.LISAAND) {
        return getLisaAndCartProducts(subscription)
    }

    return getInternalCartProducts(subscription)
}

const getLisaAndCartProducts = (_subscription: Subscription): Array<ProductProps | CartGridProps> => {
    const rows: Array<ProductProps | CartGridProps> = []

    return rows
}

const getInternalCartProducts = (subscription: Subscription): Array<ProductProps | CartGridProps> => {
    const rows: Array<ProductProps | CartGridProps> = []
    const totalRows: CartGridRowProps[] = [
        {
            left: {
                size: 'regular',
                text: t('cart.monthlyCost'),
            },
        },
    ]

    const products = subscription.products?.map((product) => {
        const servicesNames: string[] = []
        let serviceDiscountTotal = 0

        if (product.services?.length) {
            product.services.forEach((service) => {
                if (service) {
                    servicesNames.push(service.name)
                    serviceDiscountTotal += service.discountNumber
                }
            })
        }

        totalRows.push({
            left: {
                text: t(`plans.plans.${product.plan.code}.title`, {
                    months: product.planDuration,
                }),
                weight: 'regular',
            },
            right: {
                size: 'small',
                text: formatPrice(product.baseSubscriptionCostGross, subscription.currencySymbol),
            },
        })
        if (servicesNames.length) {
            totalRows.push({
                left: {
                    text: servicesNames.join(' & '),
                    weight: 'regular',
                },
                right: {
                    description: t('cart.discountDuration', {
                        months: product.planDuration,
                    }),
                    size: 'small',
                    text: formatPrice(-serviceDiscountTotal, subscription.currencySymbol),
                },
            })
        }

        if (product.defaultDiscount) {
            totalRows.push({
                left: {
                    text: t('cart.generalDiscount'),
                    weight: 'regular',
                },
                right: {
                    size: 'small',
                    text: `-${formatPrice(product.defaultDiscount, subscription.currencySymbol)}`,
                },
            })
        }

        return {
            sku: product.sku,
            content: product.name,
            description: product.sku,
            image: product.image(),
        }
    })

    if (products) {
        rows.push(...products)
    }

    rows.push({
        rows: totalRows,
    })

    if (subscription.accessories?.length) {
        const accessoryRows: CartGridRowProps[] = []
        subscription.accessories.forEach((accessory) => {
            const singleAccessoryRows: CartGridRowProps[] = [
                {
                    left: {
                        text: accessory.name!,
                        weight: 'regular',
                    },
                    right: {
                        size: 'small',
                        text: accessory.unitPrice,
                    },
                },
            ]

            if (accessory.discount) {
                singleAccessoryRows.push({
                    left: {
                        text: t('cart.generalDiscount'),
                        weight: 'regular',
                        size: 'tiny',
                    },
                    right: {
                        size: 'tiny',
                        text: `-${accessory.discount}`,
                    },
                })
            }

            for (const _index of Array(accessory.quantity)) {
                accessoryRows.push(...singleAccessoryRows)
            }
        })
        rows.push({
            rows: [
                {
                    left: {
                        size: 'regular',
                        text: t('cart.oneTimeCost'),
                    },
                },
                ...accessoryRows,
            ],
        })
    }

    if (subscription.deliveryMethod) {
        rows.push({
            rows: [
                {
                    left: {
                        text: subscription.deliveryMethod.name,
                        weight: 'regular',
                    },
                    right: {
                        size: 'small',
                        text: subscription.deliveryMethod.price,
                    },
                },
            ],
        })
    }

    return rows
}

const getCartItems = (subscription: Subscription): CartGridProps[] => {
    // ANCHOR: Decision based on InvoicingSystem
    if (subscription.data.invoicingSystem === InvoicingSystem.LISAAND) {
        return getLisaAndCartItems(subscription)
    }

    return getInternalCartItems(subscription)
}

const getLisaAndCartItems = (subscription: Subscription): CartGridProps[] => {
    const productSubscriptionRows: CartGridRowProps[] = []
    let monthlyPrice = 0

    subscription.products?.forEach((product) => {
        const productInfo = product.cartRows
        monthlyPrice += productInfo.monthly
        productSubscriptionRows.push(...productInfo.rows)
    })

    const addOnRows: CartGridRowProps[] = []
    if (subscription.data.localizedSetAddOns?.length) {
        for (const addOn of subscription.data.localizedSetAddOns) {
            monthlyPrice += addOn.price
            addOnRows.push({
                left: {
                    text: addOn.shortText,
                    weight: 'regular',
                },
                right: {
                    size: 'small',
                    text: formatPrice(addOn.price, subscription.currencySymbol),
                },
            })
        }
    }

    const rows: CartGridProps[] = [
        {
            collapsible: false,
            rows: [productSubscriptionRows[0], productSubscriptionRows[1], ...addOnRows],
            hideSeparator: true,
        },
        {
            collapsible: true,
            rows: productSubscriptionRows.slice(2),
        },
    ]

    const { grossPrice } = subscription.oneTimePrices

    const totalRows: CartGridRowProps[] = [
        {
            left: {
                size: 'small',
                text: t('lisaAnd.cart.monthlyCost'),
            },
            right: {
                size: 'small',
                text: formatMonthlyPrice(monthlyPrice, subscription.currency, subscription.locale),
            },
        },
    ]

    if (
        subscription.mainProduct.discountDuration > 0 &&
        subscription.mainProduct.discountDuration !== subscription.mainProduct.plan.duration
    ) {
        totalRows.push({
            left: {
                text: '',
            },
            right: {
                text: t('lisaAnd.plans.FLEX.priceDescription', {
                    months: subscription.mainProduct.discountDuration,
                    price: formatMonthlyPrice(
                        subscription.mainProduct.baseSubscriptionCostGross,
                        subscription.currency,
                        subscription.locale,
                    ),
                }),
                size: 'tiny',
                weight: 'regular',
            },
        })
    }

    rows.push({
        rows: totalRows,
    })

    if (subscription.accessories?.length) {
        const accessoryRows: CartGridRowProps[] = []
        subscription.accessories.forEach((accessory) => {
            const singleAccessoryRows: CartGridRowProps[] = [
                {
                    left: {
                        text: accessory.name!,
                        weight: 'regular',
                    },
                    right: {
                        size: 'small',
                        weight: 'regular',
                        text: accessory.unitPrice,
                    },
                },
            ]

            for (const _index of Array(accessory.quantity)) {
                accessoryRows.push(...singleAccessoryRows)
            }
        })
        rows.push({
            collapsible: true,
            rows: [
                {
                    left: {
                        size: 'small',
                        text: t('lisaAnd.cart.accessories'),
                        weight: 'regular',
                    },
                },
                ...accessoryRows,
            ],
        })
    }

    if (subscription.requireUpfrontPayment) {
        rows.push({
            rows: [
                {
                    left: {
                        text: t('lisaAnd.cart.prepayToday'),
                        size: 'regular',
                        weight: 'bold',
                    },
                    right: {
                        text: formatPrice(
                            grossPrice + monthlyPrice * subscription.upfrontPaymentMonths,
                            subscription.currency,
                        ),
                    },
                },
                {
                    left: {
                        text: '',
                    },
                    right: {
                        text: t('lisaAnd.cart.prepayTodayDisclaimer', {
                            months: subscription.upfrontPaymentMonths,
                        }),
                        size: 'tiny',
                        weight: 'regular',
                    },
                },
            ],
        })
    }

    return rows
}

const getInternalCartItems = (subscription: Subscription): CartGridProps[] => {
    const productSubscriptionRows: CartGridRowProps[] = []
    let monthlyPrice = 0
    let noDiscountMonthlyPrice = 0

    subscription.products?.forEach((product) => {
        const productInfo = product.cartRows
        monthlyPrice += productInfo.monthly
        noDiscountMonthlyPrice += product.data.planPriceWithVat
        productSubscriptionRows.push(...productInfo.rows)
    })

    const rows: CartGridProps[] = [
        {
            rows: [
                {
                    left: {
                        text: `${t('cart.paymentMethod')}:`,
                        list: [
                            {
                                children: `${t('cart.invoice')}`,
                            },
                            subscription.requireUpfrontPayment
                                ? {
                                      children: `${t('cart.prepay', { months: subscription.upfrontPaymentMonths })}`,
                                  }
                                : null,
                        ].filter(Boolean),
                    },
                },
            ],
        },
        {
            rows: [...productSubscriptionRows],
        },
    ]

    const { grossPrice } = subscription.oneTimePrices

    if ((subscription?.data?.nonQueryableMeta?.estimatedTradeInValue || 0) > 0) {
        const tradeInValue = subscription.data.nonQueryableMeta.estimatedTradeInValue!
        const planLength = subscription.products?.[0].planDuration || 24
        const monthly = roundPrice(tradeInValue / planLength)
        const monthlyPriceWithTradeIn = monthlyPrice - monthly

        rows.push({
            rows: [
                {
                    left: {
                        text: t('cart.tradeInTitle'),
                        weight: 'bold',
                    },
                },
                {
                    left: {
                        text: t('cart.tradeInValue'),
                        weight: 'regular',
                    },
                    right: {
                        text: formatPrice(tradeInValue, subscription.currency),
                    },
                },
                {
                    left: {
                        text: t('cart.tradeInMonthlyDiscount', {
                            duration: planLength,
                        }),
                        weight: 'regular',
                    },
                    right: {
                        text: formatPrice(-monthly, subscription.currency),
                    },
                },
            ],
        })

        rows.push({
            rows: [
                {
                    left: {
                        text: t('cart.tradeInMonthlyEstimate'),
                        size: 'regular',
                        weight: 'bold',
                    },
                    right: {
                        text: formatPrice(monthlyPriceWithTradeIn, subscription.currency),
                    },
                },
                {
                    left: {
                        text: t('cart.disclaimer'),
                        weight: 'regular',
                        size: 'tiny',
                    },
                },
            ],
        })
    } else {
        if (grossPrice > 0) {
            rows.push({
                rows: [
                    {
                        left: {
                            text: t('cart.oneTimeCostAccessories'),
                        },
                        right: {
                            text: formatPrice(grossPrice, subscription.currency),
                        },
                    },
                    {
                        left: {
                            text: t('cart.seeDetailsBelow'),
                            weight: 'regular',
                        },
                    },
                ],
            })
        }
    }

    const totalRows: CartGridRowProps[] = [
        {
            left: {
                size: subscription.requireUpfrontPayment ? 'small' : 'huge',
                text: t('cart.monthlyCostWithoutDiscount'),
                weight: subscription.requireUpfrontPayment ? 'regular' : undefined,
            },
            right: {
                size: subscription.requireUpfrontPayment ? 'regular' : 'huge',
                text: formatPrice(noDiscountMonthlyPrice, subscription.currency),
            },
        },
    ]

    if (grossPrice > 0) {
        totalRows.push({
            left: {
                text: t('cart.oneTimeCostAccessories'),
                weight: 'regular',
            },
            right: {
                text: formatPrice(grossPrice, subscription.currency),
            },
        })
    }

    if (subscription.requireUpfrontPayment) {
        totalRows.push({
            left: {
                text: t('cart.prepayToday'),
                size: 'regular',
                weight: 'bold',
            },
            right: {
                text: formatPrice(
                    grossPrice + noDiscountMonthlyPrice * subscription.upfrontPaymentMonths,
                    subscription.currency,
                ),
            },
        })
    }

    if (subscription.mainProduct) {
        let minimalTotalCost = subscription.mainProduct.minimalTotalCost
        if (subscription.country?.code === 'NO' && subscription.mainProduct.plan.code === PlanCode.VALUE) {
            const plans = Subscription.getNorwayExclusiveMinimumTotalCost(subscription)

            if (plans instanceof Array && plans) {
                const valuePlan = plans.find((plan) => plan.plan.code === PlanCode.VALUE)

                // NOTE: Nasty hack for Norway minimal total cost calculation
                minimalTotalCost = (valuePlan as SubscriptionValueProduct)?.overrideTotalMinimalCost || minimalTotalCost
            }
        }

        rows.push({
            rows: [
                {
                    left: {
                        text: t('cart.minimumTotalCost'),
                    },
                    right: {
                        text: formatPrice(minimalTotalCost, subscription.currency),
                    },
                },
            ],
        })
    }

    rows.push({
        rows: totalRows,
    })

    return rows
}

const getCartButtonText = (route: SubscriptionRoute) => {
    switch (route.step) {
        case SubscriptionSteps.FLOW_PICKER:
            return t('cart.continue')
        case SubscriptionSteps.PLANS:
            return t('cart.continue')
        case SubscriptionSteps.AUTH:
            return t('cart.authentication')
        case SubscriptionSteps.SIGN:
            return t('cart.reviewAndSign')
        case SubscriptionSteps.CREDIT_CHECK:
        case SubscriptionSteps.VALIDATION:
            return t('cart.creditCheck')
    }
}

const getSignConditionsMet = (view: View): string | true => {
    return (
        view.state.formData.firstName &&
        view.state.formData.lastName &&
        view.state.formData.address &&
        view.state.formData.email &&
        view.state.formData.phone &&
        view.state.formData.postalCode &&
        view.state.formData.city &&
        view.state.formData.country &&
        (!view.state.formData.differentShippingAddress ||
            (view.state.formData.shippingAddress &&
                view.state.formData.shippingCity &&
                view.state.formData.shippingPostalCode))
    )
}

const getCreditCheckConditionsMet = (view: View): boolean | undefined => {
    return !!view.state.formData.personalNumber && view.state.formData.creditCheckTerms
}

const getCartProps = (route: SubscriptionRoute, subscription: Subscription, view: View): CartProps | undefined => {
    const isValidationStep = route.step === SubscriptionSteps.VALIDATION
    const isPlansStep = route.step === SubscriptionSteps.PLANS
    const plansConditionsMet = !!(view.state.selectedPlan && view.state.selectedPlans.length > 0)

    const isAuthStep = route.step === SubscriptionSteps.AUTH
    const isInitialAuthStep =
        route.step === SubscriptionSteps.AUTH && view.state.flowPicked === 'existing-user' && !subscription.customer
    const authConditionsMet = !!(view.state.formData.authenticationMethod && view.state.formData.pepTerms)

    const isSignStep = route.step === SubscriptionSteps.SIGN
    const signConditionsMet = getSignConditionsMet(view)
    const isCreditCheckStep = route.step === SubscriptionSteps.CREDIT_CHECK
    const creditCheckConditionsMet = getCreditCheckConditionsMet(view)

    const isDisabled =
        (isAuthStep && !authConditionsMet) ||
        (isSignStep && !signConditionsMet) ||
        (isCreditCheckStep && !creditCheckConditionsMet) ||
        (isPlansStep && !plansConditionsMet) ||
        !subscription.currentPlan ||
        view.state.isLoading ||
        isValidationStep

    if (!isPlansStep && !view.state.selectedPlan && subscription.currentPlan) {
        view.state.selectedPlan = subscription.currentPlan.uuid!
        view.state.selectedPlans = [view.state.selectedPlan]
        view.update()
    }

    const isLisaAndFlow = subscription.invoicingSystem === InvoicingSystem.LISAAND

    if (isLisaAndFlow && view.state.flowPicked === 'existing-user' && !view.state.subscription?.customer) {
        return
    }

    return {
        cartAdditionItems: view.state.selectedPlan ? getCartProducts(subscription) : [],
        // ANCHOR: Decision based on InvoicingSystem
        cartAdditionItemsClass:
            subscription.data.invoicingSystem === InvoicingSystem.LISAAND || isInitialAuthStep
                ? 'h-hidden-md h-hidden-lg'
                : '',
        cartItems: view.state.selectedPlan && !isInitialAuthStep ? getCartItems(subscription) : [],
        cartFooter: {
            button: {
                id: 'btn-cart',
                text: getCartButtonText(route),
                isLoading: view.state.isLoading || isValidationStep,
                isDisabled,
                onClick: () => submitCart(route, subscription, view),
            },
            accordion: subscription.currentPlan
                ? {
                      children: '',
                      label: t('cart.cart'),
                      openLabel: t('cart.cart'),
                  }
                : undefined,
        },
        fraudModal:
            route.step === SubscriptionSteps.SIGN
                ? {
                      title: t('cart.fraudProtection'),
                      modal: {
                          children: '',
                          isClosable: false,
                          isOpen: view.state.fraudModalOpen,
                      },
                  }
                : undefined,
        isEmpty: !view.state.selectedPlan,
        emptyText: t('cart.emptyText'),
        noticeText: isLisaAndFlow ? '' : t('cart.noticeText'),
    }
}

const getProgressProps = (): ProgressProps => ({
    current: 1,
    items: [
        {
            label: t('sign.steps.yourInfo'),
        },
        {
            label: t('sign.steps.sign'),
        },
        {
            label: t('sign.steps.confirmation'),
        },
    ],
})

export const getSubscriptionProps = (
    route: SubscriptionRoute,
    subscription: Subscription,
    view: View,
): SubscriptionProps => {
    const isLisaandFlow = subscription.invoicingSystem === InvoicingSystem.LISAAND
    const cartProps = getCartProps(route, subscription, view)

    return {
        step: route.step,
        subscriptionUuid: subscription.uuid,
        countryTopDomain: subscription.salesChannel?.salesChannelId || '',
        basketId: subscription.basketId,
        redirectUrl: subscription.redirectUrl,
        progressProps: getProgressProps(),
        // MIXED
        selectPlanProps: getPlansProps(subscription, view),
        cartProps,
        // LISAAND
        authenticationProps: getAuthenticationProps(route, subscription, view),
        // SVEA & INTERNAL
        signProps: getSignProps(subscription, view),
        creditCheckProps: getCreditCheckProps(subscription, view),
        validationProps: getValidationProps(route, subscription, view),
        mockStatusAsAuthenticated: subscription.customer
            ? undefined
            : view.api.fetchForMockedSubscription.bind(null, subscription.uuid, MockedSubscriptionStatus.AUTHENTICATED),
        mockStatusAsSigned: subscription.customer
            ? undefined
            : view.api.fetchForMockedSubscription.bind(null, subscription.uuid, MockedSubscriptionStatus.SIGNED),
        mockStatusAsCompleted: subscription.customer
            ? undefined
            : view.api.fetchForMockedSubscription.bind(null, subscription.uuid, MockedSubscriptionStatus.COMPLETED),
        isDebugEnabled: view.state.isDebugEnabled,
        isCentered: isLisaandFlow || !cartProps,
    }
}
